import { useSearchParams } from "react-router-dom"
import {
  PoolName,
  POOLS_MAP,
  SWAP_CONTRACTS_MAP,
  SWAP_TYPES,
  TOKENS_MAP,
} from "../constants"
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import {
  SwapData,
  SwapSide,
  useCalculateSwapPairs,
} from "../hooks/useCalculateSwapPairs"
import {
  calculateExchangeRate,
  calculatePrice,
  getChainId,
  shiftBNDecimals,
} from "../utils"
import { formatUnits, parseUnits } from "@ethersproject/units"
import { useSwapContract } from "../hooks/useContract"
import { getAddress } from "../utils/addressHelpers"
import { AppState } from "../state/index"
import { BigNumber } from "@ethersproject/bignumber"
import SwapPage from "../components/SwapPage"
import { Zero } from "@ethersproject/constants"
import { calculateGasEstimate } from "../utils/gasEstimate"
import { calculatePriceImpact } from "../utils/priceImpact"
import { debounce } from "lodash"
import { formatGasToString } from "../utils/gas"
import { useApproveAndSwap } from "../hooks/useApproveAndSwap"
import { usePoolTokenBalances } from "../state/wallet/hooks"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { FixedNumber } from "ethers"
import parseStringToBigNumber, {
  sanitizeStringForFN,
} from "src/utils/parseStringToBigNumber"
import { TransactionSummaryData } from "src/components/TransactionSummary"
import { usePrevious } from "src/hooks/userPrevious"

type FormState = {
  error: null | string
  from: {
    value: string
    valueUSD: BigNumber
  } & SwapSide
  to: {
    value: BigNumber
    valueUSD: BigNumber
    valueSynth: BigNumber
  } & SwapSide
  priceImpact: BigNumber
  exchangeRate: BigNumber
  route: string[]
  swapType: SWAP_TYPES
  currentSwapPairs: SwapData[]
}
export interface TokenOption {
  symbol: string
  name: string
  valueUSD: BigNumber
  amount: BigNumber
  icon: string
  decimals: number
  swapType: SWAP_TYPES | null
  isAvailable: boolean
  address: string
}

const EMPTY_FORM_STATE: FormState = {
  error: null,
  from: {
    symbol: "",
    value: "",
    valueUSD: Zero,
  },
  to: {
    symbol: "",
    value: Zero,
    valueUSD: Zero,
    valueSynth: Zero,
  },
  priceImpact: Zero,
  exchangeRate: Zero,
  route: [],
  swapType: SWAP_TYPES.INVALID,
  currentSwapPairs: [],
}

const findBestSwap = async (
  amountToGive: BigNumber,
  fromTokenSymbol: string,
  toTokenSymbol: string,
) => {
  const tuple: [BigNumber, string] = [Zero, ""]
  const candidate: string[] = []
  const calculations = Object.keys(SWAP_CONTRACTS_MAP).reduce(
    (acc, poolName) => {
      const pool = POOLS_MAP[poolName as PoolName]
      const fromTokenIdx = pool?.poolTokens.findIndex(
        (t) => t.symbol === fromTokenSymbol,
      )
      const toTokenIdx = pool?.poolTokens.findIndex(
        (t) => t.symbol === toTokenSymbol,
      )
      if (fromTokenIdx >= 0 && toTokenIdx >= 0) {
        candidate.push(poolName)
        return [
          ...acc,
          SWAP_CONTRACTS_MAP[poolName].calculateSwap(
            fromTokenIdx,
            toTokenIdx,
            amountToGive,
          ),
        ]
      }
      return acc
    },
    [] as Array<Promise<BigNumber>>,
  )
  const res = await Promise.all([...calculations])
  res.map((amountToReceive, i) => {
    if (amountToReceive.gt(tuple[0])) {
      tuple[0] = amountToReceive
      tuple[1] = candidate[i]
    }
  })

  return tuple
}

function Swap(): ReactElement {
  const { t } = useTranslation()
  const chainId = getChainId()
  const [searchParams] = useSearchParams()
  const approveAndSwap = useApproveAndSwap()
  const tokenBalances = usePoolTokenBalances()
  const calculateSwapPairs = useCalculateSwapPairs()
  const { tokenPricesUSD, gasStandard, gasFast, gasInstant } = useSelector(
    (state: AppState) => state.application,
  )
  const { gasPriceSelected, gasCustom } = useSelector(
    (state: AppState) => state.user,
  )

  const previousTokenBalances = usePrevious(tokenBalances)
  const [formState, setFormState] = useState<FormState>(EMPTY_FORM_STATE)
  const [prevFormState, setPrevFormState] =
    useState<FormState>(EMPTY_FORM_STATE)
  useEffect(() => {
    setFormState(EMPTY_FORM_STATE)
    setPrevFormState(EMPTY_FORM_STATE)
  }, [chainId])

  const swapContract = useSwapContract(
    formState.to.poolName as PoolName | undefined,
  )

  // build a representation of pool tokens for the UI
  const tokenOptions = useMemo(() => {
    if (!chainId)
      return {
        from: [],
        to: [],
      }
    const allTokens = Object.values(TOKENS_MAP)
      .filter(({ isLPToken, addresses }) => !isLPToken && addresses[chainId])
      // .filter(({ symbol }) => {
      //   // get list of pools containing the token
      //   const tokenPools = TOKEN_TO_POOLS_MAP[symbol]
      //   // ensure at least one pool is unpaused to include token in swappable list
      //   const hasAnyUnpaused = tokenPools.reduce((acc, poolName) => {
      //     const poolStatus = poolsStatuses[poolName as PoolName]
      //     return poolStatus ? Boolean(acc || !poolStatus.isPaused) : acc
      //   }, false)
      //   return hasAnyUnpaused
      // })
      .map(({ symbol, name, icon, decimals, addresses }) => {
        const amount = tokenBalances?.[symbol] || Zero
        return {
          name,
          icon,
          symbol,
          decimals,
          amount,
          valueUSD: calculatePrice(amount, tokenPricesUSD?.[symbol], decimals),
          isAvailable: true,
          swapType: null,
          address: getAddress(addresses),
        }
      })
      .sort(sortTokenOptions)

    const toTokens =
      formState.currentSwapPairs.length > 0
        ? formState.currentSwapPairs
            .map(({ to, type: swapType }) => {
              const { symbol, name, icon, decimals, addresses } =
                TOKENS_MAP[to.symbol]
              const amount = tokenBalances?.[symbol] || Zero
              return {
                name,
                icon,
                symbol,
                decimals,
                amount,
                valueUSD: calculatePrice(
                  amount,
                  tokenPricesUSD?.[symbol],
                  decimals,
                ),
                swapType,
                isAvailable: swapType !== SWAP_TYPES.INVALID,
                address: getAddress(addresses),
              }
            })
            .sort(sortTokenOptions)
            .filter(({ isAvailable }) => isAvailable)
        : allTokens
    // from: all tokens always available. to: limited by selected "from" token.
    return {
      from: allTokens,
      to: toTokens,
    }
  }, [chainId, formState.currentSwapPairs, tokenBalances, tokenPricesUSD])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const calculateSwapAmount = useCallback(
    debounce(async (formStateArg: FormState) => {
      if (formStateArg.swapType === SWAP_TYPES.INVALID) return
      if (tokenBalances === null || chainId == null) return
      if (
        formStateArg.from.tokenIndex === undefined ||
        formStateArg.from.poolName === undefined ||
        formStateArg.to.tokenIndex === undefined ||
        formStateArg.to.poolName === undefined
      )
        return
      const cleanedFormFromValue = formStateArg.from.value.replace(/[$,]/g, "") // remove common copy/pasted financial characters
      if (
        cleanedFormFromValue === "" ||
        isNaN(+cleanedFormFromValue) ||
        formStateArg.to.symbol === ""
      ) {
        setFormState((prevState) => ({
          ...prevState,
          to: {
            ...prevState.to,
            value: Zero,
          },
          priceImpact: Zero,
        }))
        return
      }
      const amountToGive = parseUnits(
        cleanedFormFromValue,
        TOKENS_MAP[formStateArg.from.symbol].decimals,
      )
      const tokenFrom = TOKENS_MAP[formStateArg.from.symbol]
      const tokenTo = TOKENS_MAP[formStateArg.to.symbol]
      let error: string | null = null
      let amountToReceive = Zero
      let poolName: string | null = null
      let fromIndex = -1
      let toIndex = -1
      if (amountToGive.gt(tokenBalances[formStateArg.from.symbol] || Zero)) {
        error = t("Insufficient Balance")
      }
      if (amountToGive.isZero()) {
        amountToReceive = Zero
      } else if (formStateArg.swapType === SWAP_TYPES.DIRECT) {
        const bestSwap = await findBestSwap(
          amountToGive,
          formStateArg.from.symbol,
          formStateArg.to.symbol,
        )
        amountToReceive = bestSwap[0]
        poolName = bestSwap[1]
        fromIndex = POOLS_MAP[poolName as PoolName]?.poolTokens.findIndex(
          (t) => t.symbol === formStateArg.from.symbol,
        )
        toIndex = POOLS_MAP[poolName as PoolName]?.poolTokens.findIndex(
          (t) => t.symbol === formStateArg.to.symbol,
        )
      } else if (
        formStateArg.swapType === SWAP_TYPES.WRAPPED &&
        formState.to.poolName &&
        SWAP_CONTRACTS_MAP[formState.to.poolName]
      ) {
        amountToReceive = await SWAP_CONTRACTS_MAP[
          formState.to.poolName
        ].calculateSwap(
          formStateArg.from.tokenIndex,
          formStateArg.to.tokenIndex,
          amountToGive,
        )
      }
      const toValueUSD = calculatePrice(
        amountToReceive,
        tokenPricesUSD?.[tokenTo.symbol],
        tokenTo.decimals,
      )
      const priceImpact = calculatePriceImpact(
        formStateArg.from.valueUSD,
        toValueUSD,
      )
      setFormState((prevState) => {
        const newState = {
          ...prevState,
          error,
          from: {
            ...prevState.from,
            poolName: poolName ?? prevState.from?.poolName ?? "",
            tokenIndex: fromIndex >= 0 ? fromIndex : prevState.from?.tokenIndex,
          },
          to: {
            ...prevState.to,
            value: amountToReceive,
            valueUSD: toValueUSD,
            poolName: poolName ?? prevState.to?.poolName ?? "",
            tokenIndex: toIndex >= 0 ? toIndex : prevState.to?.tokenIndex,
          },
          priceImpact,
          exchangeRate: calculateExchangeRate(
            amountToGive,
            tokenFrom.decimals,
            amountToReceive,
            tokenTo.decimals,
          ),
        }
        setPrevFormState(newState)
        return newState
      })
    }, 250),
    [tokenBalances, chainId, tokenPricesUSD],
  )

  useEffect(() => {
    handleUpdateTokenFrom(searchParams.get("from") || "USDC")
    handleUpdateTokenTo(searchParams.get("to") || "DAI")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  useEffect(() => {
    // watch user input fields and calculate other fields if necessary
    if (
      prevFormState.from.symbol !== formState.from.symbol ||
      prevFormState.from.value !== formState.from.value ||
      prevFormState.to.symbol !== formState.to.symbol ||
      tokenBalances !== previousTokenBalances
    ) {
      setPrevFormState(formState)
      void calculateSwapAmount(formState)
    }
  }, [
    prevFormState,
    formState,
    calculateSwapAmount,
    tokenBalances,
    previousTokenBalances,
  ])

  function handleUpdateAmountFrom(value: string): void {
    setFormState((prevState) => {
      const nextState = {
        ...prevState,
        to: {
          ...prevState.to,
          valueUSD: Zero,
          valueSynth: Zero,
        },
        from: {
          ...prevState.from,
          value,
          valueUSD: calculatePrice(
            value,
            tokenPricesUSD?.[prevState.from.symbol],
          ),
        },
        priceImpact: Zero,
        exchangeRate: Zero,
      }
      return nextState
    })
  }

  function handleUpdateAmountTo(value: string): void {
    setFormState((prevState) => {
      const nextState: FormState = {
        ...prevState,
        to: {
          ...prevState.to,
          value: parseStringToBigNumber(
            value,
            TOKENS_MAP[prevState.to.symbol].decimals,
          ).value,
          valueUSD: calculatePrice(
            value,
            tokenPricesUSD?.[prevState.to.symbol],
          ),
          valueSynth: Zero,
        },
        from: {
          ...prevState.from,
          valueUSD: Zero,
        },
        priceImpact: Zero,
        exchangeRate: Zero,
      }
      return nextState
    })
  }

  function handleReverseExchangeDirection(): void {
    if (!formState.from.symbol || !formState.to.symbol) return
    setFormState((prevState) => {
      const swapPairs = calculateSwapPairs(
        TOKENS_MAP[prevState.to.symbol],
        prevState.to.poolName as PoolName,
      )
      const activeSwapPair = swapPairs.find(
        (pair) => pair.to.symbol === prevState.from.symbol,
      )
      const nextState = {
        error: null,
        from: {
          symbol: prevState.to.symbol,
          value: FixedNumber.from(sanitizeStringForFN(prevState.from.value))
            .round(TOKENS_MAP[prevState.to.symbol].decimals)
            .toString(),
          valueUSD: calculatePrice(
            prevState.from.value,
            tokenPricesUSD?.[prevState.to.symbol],
          ),
          poolName: activeSwapPair?.from.poolName,
          tokenIndex: activeSwapPair?.from.tokenIndex,
        },
        to: {
          symbol: prevState.from.symbol,
          value: Zero,
          valueUSD: Zero,
          valueSynth: Zero,
          poolName: activeSwapPair?.to.poolName,
          tokenIndex: activeSwapPair?.to.tokenIndex,
        },
        priceImpact: Zero,
        exchangeRate: Zero,
        route: activeSwapPair?.route || [],
        currentSwapPairs: swapPairs,
        swapType: activeSwapPair?.type || SWAP_TYPES.INVALID,
      }
      return nextState
    })
  }
  function handleUpdateTokenFrom(symbol: string): void {
    if (symbol === formState.to.symbol) return handleReverseExchangeDirection()
    setFormState((prevState) => {
      const swapPairs = calculateSwapPairs(
        TOKENS_MAP[symbol],
        prevState.to.poolName as PoolName,
      )
      const activeSwapPair = swapPairs.find(
        (pair) => pair.to.symbol === prevState.to.symbol,
      )
      const nextState = {
        ...prevState,
        error: null,
        from: {
          ...prevState.from,
          symbol,
          valueUSD: calculatePrice(
            prevState.from.value,
            tokenPricesUSD?.[symbol],
          ),
          poolName: activeSwapPair?.from.poolName,
          tokenIndex: activeSwapPair?.from.tokenIndex,
        },
        to: {
          ...prevState.to,
          value: Zero,
          valueSynth: Zero,
          valueUSD: Zero,
          symbol:
            activeSwapPair?.type === SWAP_TYPES.INVALID
              ? ""
              : prevState.to.symbol,
          poolName: activeSwapPair?.to.poolName,
          tokenIndex: activeSwapPair?.to.tokenIndex,
        },
        priceImpact: Zero,
        exchangeRate: Zero,
        route: activeSwapPair?.route || [],
        currentSwapPairs: swapPairs,
        swapType: activeSwapPair?.type || SWAP_TYPES.INVALID,
      }
      return nextState
    })
  }

  function handleUpdateTokenTo(symbol: string): void {
    if (symbol === formState.from.symbol)
      return handleReverseExchangeDirection()
    setFormState((prevState) => {
      const activeSwapPair = prevState.currentSwapPairs.find(
        (pair) => pair.to.symbol === symbol,
      )
      const nextState = {
        ...prevState,
        from: {
          ...prevState.from,
          ...(activeSwapPair?.from || {}),
        },
        error: null,
        to: {
          ...prevState.to,
          value: Zero,
          valueSynth: Zero,
          symbol,
          valueUSD: Zero,
          poolName: activeSwapPair?.to.poolName,
          tokenIndex: activeSwapPair?.to.tokenIndex,
        },
        priceImpact: Zero,
        exchangeRate: Zero,
        route: activeSwapPair?.route || [],
        swapType: activeSwapPair?.type || SWAP_TYPES.INVALID,
      }
      return nextState
    })
  }

  async function handleConfirmTransaction(
    summary: TransactionSummaryData,
  ): Promise<void> {
    const fromToken = TOKENS_MAP[formState.from.symbol]
    if (
      formState.swapType === SWAP_TYPES.INVALID ||
      formState.from.tokenIndex === undefined ||
      formState.from.poolName === undefined ||
      formState.to.tokenIndex === undefined ||
      formState.to.poolName === undefined
    ) {
      console.debug("Invalid transaction", formState)
      setFormState((prevState) => ({
        ...EMPTY_FORM_STATE,
        error: "Invalid Transaction",
        from: {
          ...prevState.from,
          value: "0.0",
          valueUSD: Zero,
        },
      }))
      return
    }
    await approveAndSwap(
      {
        swapContract,
        from: {
          amount: parseUnits(formState.from.value, fromToken.decimals),
          symbol: formState.from.symbol,
          poolName: formState.from.poolName,
          tokenIndex: formState.from.tokenIndex,
        },
        to: {
          amount: formState.to.value,
          symbol: formState.to.symbol,
          poolName: formState.to.poolName,
          tokenIndex: formState.to.tokenIndex,
          amountMediumSynth: formState.to.valueSynth,
        },
        swapType: formState.swapType,
      },
      summary,
    )
    // Clear input after deposit
    setFormState((prevState) => ({
      error: null,
      from: {
        ...prevState.from,
        value: "",
        valueUSD: Zero,
      },
      to: {
        ...prevState.to,
        value: Zero,
        valueUSD: Zero,
      },
      priceImpact: Zero,
      exchangeRate: Zero,
      route: prevState.route,
      currentSwapPairs: prevState.currentSwapPairs,
      swapType: prevState.swapType,
    }))
  }

  const gasPrice = BigNumber.from(
    formatGasToString(
      { gasStandard, gasFast, gasInstant },
      gasPriceSelected,
      gasCustom,
    ),
  )
  const gasAmount = calculateGasEstimate(formState.swapType).mul(gasPrice) // units of gas * GWEI/Unit of gas

  const txnGasCost = {
    amount: gasAmount,
    valueUSD: tokenPricesUSD?.ETH
      ? parseUnits(tokenPricesUSD.ETH.toFixed(2), 18) // USD / ETH  * 10^18
          .mul(gasAmount) // GWEI
          .div(BigNumber.from(10).pow(25)) // USD / ETH * GWEI * ETH / GWEI = USD
      : null,
  }

  return (
    <SwapPage
      tokenOptions={tokenOptions}
      exchangeRateInfo={{
        pair: `${formState.from.symbol}/${formState.to.symbol}`,
        exchangeRate: formState.exchangeRate,
        priceImpact: formState.priceImpact,
        route: formState.route,
      }}
      txnGasCost={txnGasCost}
      fromState={formState.from}
      toState={{
        ...formState.to,
        value:
          formState.to.symbol === "" || formState.to.value.eq(0)
            ? "0"
            : formatUnits(
                formState.to.value,
                TOKENS_MAP[formState.to.symbol].decimals,
              ),
        rawAmount: formState.to.value,
        poolName: formState.to.poolName ?? "",
      }}
      swapType={formState.swapType}
      onChangeFromAmount={handleUpdateAmountFrom}
      onChangeFromToken={handleUpdateTokenFrom}
      onChangeToToken={handleUpdateTokenTo}
      error={formState.error}
      onConfirmTransaction={handleConfirmTransaction}
      onClickReverseExchangeDirection={handleReverseExchangeDirection}
      onChangeToAmount={handleUpdateAmountTo}
    />
  )
}

export default Swap

const sortTokenOptions = (a: TokenOption, b: TokenOption) => {
  if (a.isAvailable !== b.isAvailable) {
    return a.isAvailable ? -1 : 1
  }
  // if either is invalid, put the valid one first
  if (a.swapType === SWAP_TYPES.INVALID || b.swapType === SWAP_TYPES.INVALID) {
    return a.swapType === SWAP_TYPES.INVALID ? 1 : -1
  }
  if (a.valueUSD.gt(b.valueUSD)) {
    // prefer largest wallet balance
    return -1
  } else if (a.valueUSD.gt(Zero) && a.valueUSD.eq(b.valueUSD)) {
    const amountA = shiftBNDecimals(a.amount, 18 - a.decimals)
    const amountB = shiftBNDecimals(b.amount, 18 - b.decimals)
    return amountA.gt(amountB) ? -1 : 1
  }
  // prefer direct swaps
  return a.swapType === SWAP_TYPES.DIRECT ? -1 : 1
}
